import * as React from "react";
import {
    Modal, Box, Typography, IconButton, Grid, TextField, Select, MenuItem,
    Divider, Stack, InputAdornment,
    Skeleton,
    Autocomplete
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CtaButton from "../buttonWithArrow";
import { getPrivateJetListNew } from '../../redux/slices/common';
import { useDispatch, useSelector } from '../../redux/store'
import { getEcommerceListQuery } from "../../redux/slices/investments";
import { CurrencySimple, useResponsiveValue } from "../../utils/helper";
import { lockScroll, unlockScroll } from "../../utils/scrollLock";
import { countries } from '../../data/countries';

function JetSkeletonCard() {
    return (
        <>
            <Box>
                <Skeleton
                    variant="rectangular"
                    height={100}
                    sx={{ borderRadius: "12px" }}
                />
                <Skeleton
                    variant="text"
                    width="60%"
                    height={18}
                    sx={{ mx: "auto", mt: 1 }}
                />
            </Box>
        </>
    );
}

// utils/sanitizeMoney.ts
export function sanitizeToTwoDecimals(input) {
    if (!input) return "";

    // Keep digits and dots only
    let v = input.replace(/[^\d.]/g, "");

    // Keep only the first dot
    const firstDot = v.indexOf(".");
    if (firstDot !== -1) {
        v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
    }

    // Split integer/decimal
    let [intPart, decPart = ""] = v.split(".");

    // Remove leading zeros like "0005" -> "5" (but keep "0")
    intPart = intPart.replace(/^0+(?=\d)/, "");

    // Cap to 2 decimal places
    decPart = decPart.slice(0, 2);

    // Rebuild
    if (firstDot !== -1) {
        return `${intPart === "" ? "0" : intPart}.${decPart}`;
    }
    return intPart;
}


// ---------- helpers ----------
const CURRENCY = {
    USD: { sign: "$", rate: 1, type: "string" },
    AED: { sign: "images/bannerImage/1758795455186.svg", rate: 3.67, type: "image" },
    // EUR: { sign: "€", rate: 0.92 },
};

const fmt = (amount, count) => {
    const total = (amount || 0) * (count || 0);
    return `${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

// pill look
const pillInputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 999 },
};

// stat pill
const statSx = {
    p: 2,
    borderRadius: 2.5,
    border: "1px solid #E6E8EC",
    bgcolor: "#fff",
    textAlign: "center",
};

const TEAL_BOTTOM = "#0D8196";

const jetTileSx = (active) => ({
    objectFit: "contain", borderRadius: 3, "&:hover": { border: "2px solid #0d8196" },
    border: active ? "2px solid #0d8196" : "none"
});

const jetLabelSx = (active) => ({
    textAlign: "center", mt: 0.75, fontWeight: 700, fontSize: 13,
    color: active ? TEAL_BOTTOM : "#111827",
});

const jetPriceSx = (active) => ({
    textAlign: "center", mt: 0.75, fontWeight: 700, fontSize: 13,
    color: active ? TEAL_BOTTOM : "#111827",
    display: "flex",
    justifyContent: "center",
    alignItems: 'center'
});

// ---------- main component ----------
export default function InvestmentSimulatorFlow({
    open,
    onClose,
    onInvest, // (payload) => void
    values,
    setValues,
    formLoader, listData, priceData
}) {
    const dispatch = useDispatch();
    const [step, setStep] = React.useState(1);
    const [amount, setAmount] = React.useState("");
    const [currency, setCurrency] = React.useState("AED");
    const [selected, setSelected] = React.useState(null);
    const [loader, setLoader] = React.useState(false);
    const [eligible, setEligible] = React.useState([]);
    const { ecommerceListQueryRes } = useSelector(
        state => state.investment
    );
    const parsedAmount = Number(String(amount).replace(/,/g, "")) || 0;
    const [error, setError] = React.useState({
        name: false,
        email: false,
        countryCode: false,
        phone: false,
        editedTotalPrice: false,
        editedROI: false
    });

    const responsiveFontSize = useResponsiveValue({ xs: 16, sm: 16, lg: 16 });
    const responsiveSelectFont = useResponsiveValue({ xs: 15, sm: 15, lg: 15 });
    const responsiveSelectPrice = useResponsiveValue({ xs: 13, sm: 13, lg: 13 });
    const responsiveIconSize = useResponsiveValue({ xs: 18, sm: 26 });

    React.useEffect(() => {
        if (amount && currency) {
            const obj = {
                amount: amount,
                currency: currency
            }
            setLoader(true);
            dispatch(getEcommerceListQuery(obj))
        }
    }, [amount, currency])

    React.useEffect(() => {
        if (ecommerceListQueryRes) {
            setLoader(false);
            if (ecommerceListQueryRes?.status === 1) {
                setEligible(ecommerceListQueryRes?.responseData)
            } else {
                setEligible([]);
            }
        }
    }, [ecommerceListQueryRes])

    React.useEffect(() => {
        if (open) {
            lockScroll()
            resetAll();
        }
        else {
            unlockScroll();
        }

        return () => unlockScroll(); // cleanup if component unmounts
    }, [open]);

    const resetAll = () => {
        setStep(1);
        setSelected(null);
        setAmount("");
        setCurrency("AED");
    };

    const closeAll = () => { onClose(); setTimeout(resetAll, 300); };

    const errors = React.useMemo(() => {
        const errs = {};

        // User name
        if (!values.name.trim()) {
            errs.name = "Name is required";
        }

        // Email
        if (!values.email.trim()) {
            errs.email = "Email is required";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(values.email)) {
                errs.email = "Enter a valid email address";
            }
        }

        // Phone
        if (!values.phone.trim()) {
            errs.phone = "Phone number is required";
        } else {
            const phoneRegex = /^[0-9]{6,15}$/; // 6–15 digits
            if (!phoneRegex.test(values.phone)) {
                errs.phone = "Enter a valid phone number";
            }
        }

        // User name
        if (!values.editedTotalPrice.trim()) {
            errs.editedTotalPrice = "This field is required";
        }

        // User name
        if (!values.editedROI.trim()) {
            errs.editedROI = "This field is required";
        }

        return errs;
    }, [values]);

    const isValid = React.useMemo(() => Object.keys(errors).length === 0, [errors]);

    const handleSubmit = () => {
        setError(
            {
                name: true,
                email: true,
                countryCode: true,
                phone: true,
                editedTotalPrice: true,
                editedROI: true
            }
        );
        if (!isValid) return;
        setError(
            {
                name: false,
                email: false,
                countryCode: false,
                phone: false,
                editedTotalPrice: false,
                editedROI: false

            }
        );
        onInvest?.(values);
    };

    return (
        <Modal open={open} onClose={closeAll}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
            <Box sx={{ position: "relative", width: 820, maxWidth: "95vw", bgcolor: "#fff", borderRadius: 4, p: 3 }}>
                {/* close */}
                <IconButton onClick={closeAll} sx={{ position: "absolute", top: 8, right: 8 }}>
                    <CloseRoundedIcon />
                </IconButton>

                {/* ---------------------- STEP HEADER ---------------------- */}
                {step !== 2 ? (
                    <>
                        <Typography className="jura" sx={{ fontWeight: 800, fontSize: 24, textAlign: "center" }}>
                            Investment calculator simulator
                        </Typography>
                        <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                            Enter your amount to see tailored options where you can invest and grow with RSL.
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography className="jura" sx={{ fontWeight: 800, fontSize: 24, textAlign: "center" }}>
                            {selected?.title}
                        </Typography>
                        <Typography sx={{ color: "#6b7280", textAlign: "center", mt: 1, mb: 2 }}>
                            These are the approx. values you can get with the product that you choosed
                        </Typography>
                    </>
                )}

                {/* ---------------------- STEP 1 & 2 ---------------------- */}
                {step !== 2 && step === 1 && (
                    <Box sx={{ border: "1px solid #dddce0", borderRadius: 2.5, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Amount</Typography>
                                <Select
                                    fullWidth
                                    displayEmpty
                                    value={amount ?? ""}
                                    onChange={(e) => setAmount(e.target.value)}
                                    sx={{
                                        ...pillInputSx,
                                        "& fieldset": { borderRadius: 999 },
                                    }}
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return (
                                                <Box sx={{ color: "rgba(0,0,0,0.5)" }}>
                                                    Select Amount
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                {CurrencySimple(
                                                    null,
                                                    null,
                                                    responsiveSelectFont,
                                                    CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string"
                                                        ? CURRENCY[currency]?.sign
                                                        : null
                                                )}
                                                {selected}
                                            </Box>
                                        );
                                    }}
                                >
                                    {/* placeholder item (hidden but keeps accessibility) */}
                                    <MenuItem value="">
                                        <em>Select Amount</em>
                                    </MenuItem>

                                    {priceData?.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {CurrencySimple(
                                                    null,
                                                    null,
                                                    responsiveSelectFont,
                                                    CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string"
                                                        ? CURRENCY[currency]?.sign
                                                        : null
                                                )}
                                                {c}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Currency</Typography>
                                <Select
                                    fullWidth
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    sx={{ ...pillInputSx, "& fieldset": { borderRadius: 999 } }}
                                    renderValue={(v) => (
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            {CURRENCY[v].type === "string" ? (
                                                <>{CURRENCY[v].sign}</>
                                            ) : (
                                                <img
                                                    src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[v].sign)}`}
                                                    alt="currency"
                                                    style={{ width: "1em", height: "1em", objectFit: "contain" }}
                                                />
                                            )}
                                            {v}
                                        </Box>
                                    )}
                                >
                                    {Object.keys(CURRENCY).map((c) => (
                                        <MenuItem key={c} value={c}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                {CURRENCY[c].type === "string" ? (
                                                    <>{CURRENCY[c].sign}</>
                                                ) : (
                                                    <img
                                                        src={`${process.env.IMAGE_BASE_URL}/${String(CURRENCY[c].sign)}`}
                                                        alt="currency"
                                                        style={{ width: "1em", height: "1em", objectFit: "contain" }}
                                                    />
                                                )}
                                                {c}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </Grid>

                        {parsedAmount > 0 && (
                            <>
                                <Divider sx={{ my: 2, borderColor: "#a1a1a1" }} />
                                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                    These are the options you can invest in <Box component="span" sx={{ color: "#9CA3AF", fontWeight: 500 }}>(Choose one to continue)</Box>
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        overflowX: "auto",
                                        py: 1,
                                        px: 1,
                                        mt: 2,
                                        "&::-webkit-scrollbar": {
                                            height: 6,
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#ccc",
                                            borderRadius: 3,
                                        },
                                    }}
                                >
                                    {loader &&
                                        [...Array(6)].map((_, i) => (
                                            <Box key={i} sx={{ flex: "0 0 auto", width: 200 }}>
                                                <JetSkeletonCard />
                                            </Box>
                                        ))}

                                    {!loader && eligible.length === 0 && (
                                        <Box sx={{ color: "#9CA3AF", textAlign: "center", py: 2, flex: "0 0 100%" }}>
                                            No options match this amount yet. Try a higher amount.
                                        </Box>
                                    )}

                                    {!loader &&
                                        eligible.map((j) => {
                                            const active = false; // replace with your active logic
                                            return (
                                                <Box
                                                    key={j?._id}
                                                    sx={{
                                                        flex: "0 0 auto",
                                                        width: 150,
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        flexDirection: "column"
                                                    }}
                                                    onClick={() => {
                                                        setSelected(j);
                                                        setStep(2);
                                                        setValues({
                                                            ...values,
                                                            itemInfo: j,
                                                            itemId: j?._id,
                                                            editedTotalPrice: j?.price,
                                                            editedROI: j?.price
                                                        });
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        alt={j?.title}
                                                        src={`${process.env.IMAGE_BASE_URL}/${j?.image}`}
                                                        sx={jetTileSx(active)}
                                                    />
                                                    <Typography sx={jetLabelSx(active)}>{j?.title}</Typography>
                                                    <Typography sx={jetPriceSx(active)}>({CurrencySimple(null, active ? "#0D8196" : "#111827", responsiveSelectPrice, CURRENCY[currency]?.type,
                                                        CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}{j?.price})</Typography>
                                                </Box>
                                            );
                                        })}
                                </Box>

                            </>
                        )}
                    </Box>
                )}

                {/* ---------------------- STEP 3 (DETAIL) ---------------------- */}
                {step === 2 && selected && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                mt: 3,
                                flexWrap: "wrap", // responsive
                            }}
                        >
                            {/* Left Side */}
                            <Box sx={{ flex: "0 0 200px", textAlign: "center", minWidth: "250px" }}>
                                <Box
                                    component="img"
                                    src={`${process.env.IMAGE_BASE_URL}/${selected?.image}`}
                                    alt="img"
                                    sx={{
                                        width: 250,
                                        height: 250,
                                        borderRadius: 2,
                                        objectFit: "cover",
                                    }}
                                />
                                <Typography
                                    sx={{
                                        mt: 1,
                                        fontSize: 13,
                                        color: "#6B7280",
                                        textAlign: "start"
                                    }}
                                >
                                    Given Values are approximate, subject to change based on market factors*
                                </Typography>
                            </Box>

                            {/* Right Side */}
                            <Box sx={{ flex: 1, minWidth: 280 }}>
                                {/* Congratulations Section */}
                                <Typography sx={{ color: "#0D8196", fontWeight: 600, mb: 0.5 }}>
                                    Congratulations!
                                </Typography>
                                <Typography sx={{ mb: 2 }}>
                                    You choose <b>{selected?.title}</b> for investment.
                                </Typography>

                                {/* Amount */}
                                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Total Amount you want to invest
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={values?.editedTotalPrice}
                                    sx={{
                                        mb: 2,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 999,
                                        },
                                    }}
                                    onChange={(e) => {
                                        const value = sanitizeToTwoDecimals(e.target.value);
                                        setValues((prev) => ({ ...prev, editedTotalPrice: value }));
                                    }}
                                    error={error.editedTotalPrice && !!errors.editedTotalPrice}
                                    helperText={error.editedTotalPrice && errors.editedTotalPrice ? errors.editedTotalPrice : " "}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {CurrencySimple(null, "#0000008a", responsiveFontSize, CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}
                                            </InputAdornment>

                                        ),
                                    }}
                                />

                                {/* ROI */}
                                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Total Expected ROI / Per Month
                                </Typography>
                                <TextField
                                    fullWidth
                                    value={values?.editedROI}
                                    sx={{
                                        mb: 3,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 999,
                                        },
                                    }}
                                    onChange={(e) => {
                                        const value = sanitizeToTwoDecimals(e.target.value);
                                        setValues((prev) => ({ ...prev, editedROI: value }));
                                    }}
                                    error={error.editedROI && !!errors.editedROI}
                                    helperText={error.editedROI && errors.editedROI ? errors.editedROI : " "}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {CurrencySimple(null, "#0000008a", responsiveFontSize, CURRENCY[currency]?.type,
                                                    CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}
                                            </InputAdornment>

                                        ),
                                    }}
                                />

                                {/* User Info */}
                                <Typography sx={{ color: "#0D8196", fontWeight: 600, mb: 1 }}>
                                    User Information
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Name</Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="Enter Name"
                                            name="name"
                                            value={values.name}
                                            error={error.name && !!errors.name}
                                            helperText={error.name && errors.name ? errors.name : " "}
                                            onChange={(e) => setValues({ ...values, name: e.target.value })}
                                            sx={pillInputSx}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Email</Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="Enter Email"
                                            value={values.email}
                                            name="email"
                                            error={error.email && !!errors.email}
                                            helperText={error.email && errors.email ? errors.email : " "}
                                            onChange={(e) => setValues({ ...values, email: e.target.value })}
                                            sx={pillInputSx}
                                        />

                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Phone</Typography>
                                        <Box sx={{ display: "flex", mb: 2 }}>
                                            {/* Country Code Selector */}
                                            <Autocomplete
                                                disableClearable
                                                options={countries}
                                                getOptionLabel={(option) => `${option.code} (+${option.phone})`}
                                                filterOptions={(options, { inputValue }) =>
                                                    options.filter(
                                                        (option) =>
                                                            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                            option.code.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                            option.phone.includes(inputValue.replace("+", ""))
                                                    )
                                                }
                                                value={countries.find((c) => c.phone === values.countryCode) || countries[0]}
                                                onChange={(e, value) => {
                                                    if (value) setValues({ ...values, countryCode: value.phone });
                                                }}
                                                sx={{
                                                    width: "35%",
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "999px 0 0 999px",
                                                    },
                                                }}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <span style={{ marginRight: 8 }}>
                                                            {String.fromCodePoint(
                                                                ...[...option.code.toUpperCase()].map((c) => 127397 + c.charCodeAt())
                                                            )}
                                                        </span>
                                                        {option.code} (+{option.phone})
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "999px 0 0 999px",
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />

                                            {/* Phone Number Input */}
                                            <TextField
                                                fullWidth
                                                placeholder="Enter Phone"
                                                value={values.phone}
                                                name="phone"
                                                onChange={(e) => setValues({ ...values, phone: e.target.value })}
                                                error={error.phone && !!errors.phone}
                                                helperText={error.phone && errors.phone ? errors.phone : " "}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "0 999px 999px 0", // rounded right side
                                                        bgcolor: "#fff",
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                            <CtaButton label="Back" iconPosition="start" onClick={() => setStep(step - 1)} variant="secondary" sx={{ boxShadow: "none !important" }} endIconSrc="/img/header/VectorLeft.svg" />
                            <CtaButton label="Invest Now" loader={formLoader} onClick={handleSubmit} />
                        </Box>
                    </>
                )}

                {/* {
                    step === 3 &&
                    <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontWeight: 700 }}>Personal Details</Typography>
                        <Grid container spacing={2} sx={{ mt: 0 }}>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Name</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Enter Name"
                                    name="name"
                                    value={values.name}
                                    error={error.name && !!errors.name}
                                    helperText={error.name && errors.name ? errors.name : " "}
                                    onChange={(e) => setValues({ ...values, name: e.target.value })}
                                    sx={pillInputSx}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Email</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Enter Email"
                                    value={values.email}
                                    name="email"
                                    error={error.email && !!errors.email}
                                    helperText={error.email && errors.email ? errors.email : " "}
                                    onChange={(e) => setValues({ ...values, email: e.target.value })}
                                    sx={pillInputSx}
                                />

                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={{ fontWeight: 500, mb: 0.75 }}>Phone</Typography>
                                <Box sx={{ display: "flex", mb: 2 }}>                                    
                                    <Autocomplete
                                        disableClearable
                                        options={countries}
                                        getOptionLabel={(option) => `${option.code} (+${option.phone})`}
                                        filterOptions={(options, { inputValue }) =>
                                            options.filter(
                                                (option) =>
                                                    option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.code.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                    option.phone.includes(inputValue.replace("+", ""))
                                            )
                                        }
                                        value={countries.find((c) => c.phone === values.countryCode) || countries[0]}
                                        onChange={(e, value) => {
                                            if (value) setValues({ ...values, countryCode: value.phone });
                                        }}
                                        sx={{
                                            width: "35%",
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "999px 0 0 999px",
                                            },
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <span style={{ marginRight: 8 }}>
                                                    {String.fromCodePoint(
                                                        ...[...option.code.toUpperCase()].map((c) => 127397 + c.charCodeAt())
                                                    )}
                                                </span>
                                                {option.code} (+{option.phone})
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "999px 0 0 999px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        placeholder="Enter Phone"
                                        value={values.phone}
                                        name="phone"
                                        onChange={(e) => setValues({ ...values, phone: e.target.value })}
                                        error={error.phone && !!errors.phone}
                                        helperText={error.phone && errors.phone ? errors.phone : " "}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "0 999px 999px 0", // rounded right side
                                                bgcolor: "#fff",
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>

                        </Grid>
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <CtaButton label="Invest Now" loader={formLoader} onClick={handleSubmit} />
                        </Box>
                    </Box>
                } */}
            </Box>
        </Modal >
    );
}

// ---------- tiny subcomponents ----------
// Replace your Chiplet with this
function Chiplet({ label }) {
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                px: 2.5,
                py: 1,
                borderRadius: 2.5, // ≈10px
                // gradient outline over white background
                border: "2px solid transparent",
                background:
                    "linear-gradient(#fff,#fff) padding-box, linear-gradient(180deg,#8AD9D9 0%, #0D8196 100%) border-box",
                color: "#0D8196",
                fontWeight: 600,
                fontSize: { xs: 14, sm: 16 }, // adjust to taste
                lineHeight: 1.2,
            }}
        >
            {label}
        </Box>
    );
}


function Stat({ title, value, type, sign, teal = false }) {
    return (
        <Box sx={statSx}>
            <Typography sx={{ color: "#6b7280", fontWeight: 700, mb: 0.5, fontSize: { xs: 12, sm: 16 } }}>{title}</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, sm: 28 }, color: teal ? "#0D8196" : "#111827" }}>
                {type && sign && (
                    type === "string" ? (
                        <>{sign}</>
                    ) : (
                        <img
                            src={`${process.env.IMAGE_BASE_URL}/${String(sign)}`}
                            alt="currency"
                            style={{ width: "1em", height: "1em", objectFit: "contain" }}
                        />
                    )
                )}
                {value}
            </Typography>
        </Box>
    );
}
